import type { JoinResponse, Product, ProductsResponse, FeedbackPayload, FeedbackResponse, ApiError, RecordingConsentResponse, CustomerJoinedResponse, BasketResponse, BasketProduct, WishProduct, WishProductsResponse, ProductDetailResponse, ProductDetail, ProductVariant, WidgetAuth, CallStopPayload } from '../types/api';
import type { CommunicationMode } from '../types/app';
import { env } from '../config/env';
import { captureError } from './errorTracking';
import { MOCK_JOIN_RESPONSE, MOCK_PRODUCTS, MOCK_BASKET_RESPONSE, MOCK_WISH_PRODUCTS_RESPONSE, MOCK_PRODUCT_DETAIL, MOCK_PRODUCT_DETAIL_NO_VARIANTS, MOCK_COMBO_PARENT_PRODUCT, MOCK_COMBO_CHILD_1, MOCK_COMBO_CHILD_2 } from './mockData';

/**
 * Inline MD5 implementation for Widget API X-Signature header.
 * SubtleCrypto does NOT support MD5 -- only SHA-1, SHA-256, SHA-384, SHA-512.
 * This is a self-contained pure JS implementation.
 */
function md5(input: string): string {
  function safeAdd(x: number, y: number): number {
    const lsw = (x & 0xffff) + (y & 0xffff);
    const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
    return (msw << 16) | (lsw & 0xffff);
  }
  function bitRotateLeft(num: number, cnt: number): number {
    return (num << cnt) | (num >>> (32 - cnt));
  }
  function md5cmn(q: number, a: number, b: number, x: number, s: number, t: number): number {
    return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b);
  }
  function md5ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
    return md5cmn((b & c) | (~b & d), a, b, x, s, t);
  }
  function md5gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
    return md5cmn((b & d) | (c & ~d), a, b, x, s, t);
  }
  function md5hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
    return md5cmn(b ^ c ^ d, a, b, x, s, t);
  }
  function md5ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
    return md5cmn(c ^ (b | ~d), a, b, x, s, t);
  }

  function binlMD5(x: number[], len: number): number[] {
    x[len >> 5] |= 0x80 << (len % 32);
    x[(((len + 64) >>> 9) << 4) + 14] = len;
    let a = 1732584193, b = -271733879, c = -1732584194, d = 271733878;
    for (let i = 0; i < x.length; i += 16) {
      const olda = a, oldb = b, oldc = c, oldd = d;
      a = md5ff(a, b, c, d, x[i], 7, -680876936);
      d = md5ff(d, a, b, c, x[i + 1], 12, -389564586);
      c = md5ff(c, d, a, b, x[i + 2], 17, 606105819);
      b = md5ff(b, c, d, a, x[i + 3], 22, -1044525330);
      a = md5ff(a, b, c, d, x[i + 4], 7, -176418897);
      d = md5ff(d, a, b, c, x[i + 5], 12, 1200080426);
      c = md5ff(c, d, a, b, x[i + 6], 17, -1473231341);
      b = md5ff(b, c, d, a, x[i + 7], 22, -45705983);
      a = md5ff(a, b, c, d, x[i + 8], 7, 1770035416);
      d = md5ff(d, a, b, c, x[i + 9], 12, -1958414417);
      c = md5ff(c, d, a, b, x[i + 10], 17, -42063);
      b = md5ff(b, c, d, a, x[i + 11], 22, -1990404162);
      a = md5ff(a, b, c, d, x[i + 12], 7, 1804603682);
      d = md5ff(d, a, b, c, x[i + 13], 12, -40341101);
      c = md5ff(c, d, a, b, x[i + 14], 17, -1502002290);
      b = md5ff(b, c, d, a, x[i + 15], 22, 1236535329);
      a = md5gg(a, b, c, d, x[i + 1], 5, -165796510);
      d = md5gg(d, a, b, c, x[i + 6], 9, -1069501632);
      c = md5gg(c, d, a, b, x[i + 11], 14, 643717713);
      b = md5gg(b, c, d, a, x[i], 20, -373897302);
      a = md5gg(a, b, c, d, x[i + 5], 5, -701558691);
      d = md5gg(d, a, b, c, x[i + 10], 9, 38016083);
      c = md5gg(c, d, a, b, x[i + 15], 14, -660478335);
      b = md5gg(b, c, d, a, x[i + 4], 20, -405537848);
      a = md5gg(a, b, c, d, x[i + 9], 5, 568446438);
      d = md5gg(d, a, b, c, x[i + 14], 9, -1019803690);
      c = md5gg(c, d, a, b, x[i + 3], 14, -187363961);
      b = md5gg(b, c, d, a, x[i + 8], 20, 1163531501);
      a = md5gg(a, b, c, d, x[i + 13], 5, -1444681467);
      d = md5gg(d, a, b, c, x[i + 2], 9, -51403784);
      c = md5gg(c, d, a, b, x[i + 7], 14, 1735328473);
      b = md5gg(b, c, d, a, x[i + 12], 20, -1926607734);
      a = md5hh(a, b, c, d, x[i + 5], 4, -378558);
      d = md5hh(d, a, b, c, x[i + 8], 11, -2022574463);
      c = md5hh(c, d, a, b, x[i + 11], 16, 1839030562);
      b = md5hh(b, c, d, a, x[i + 14], 23, -35309556);
      a = md5hh(a, b, c, d, x[i + 1], 4, -1530992060);
      d = md5hh(d, a, b, c, x[i + 4], 11, 1272893353);
      c = md5hh(c, d, a, b, x[i + 7], 16, -155497632);
      b = md5hh(b, c, d, a, x[i + 10], 23, -1094730640);
      a = md5hh(a, b, c, d, x[i + 13], 4, 681279174);
      d = md5hh(d, a, b, c, x[i + 0], 11, -358537222);
      c = md5hh(c, d, a, b, x[i + 3], 16, -722521979);
      b = md5hh(b, c, d, a, x[i + 6], 23, 76029189);
      a = md5hh(a, b, c, d, x[i + 9], 4, -640364487);
      d = md5hh(d, a, b, c, x[i + 12], 11, -421815835);
      c = md5hh(c, d, a, b, x[i + 15], 16, 530742520);
      b = md5hh(b, c, d, a, x[i + 2], 23, -995338651);
      a = md5ii(a, b, c, d, x[i], 6, -198630844);
      d = md5ii(d, a, b, c, x[i + 7], 10, 1126891415);
      c = md5ii(c, d, a, b, x[i + 14], 15, -1416354905);
      b = md5ii(b, c, d, a, x[i + 5], 21, -57434055);
      a = md5ii(a, b, c, d, x[i + 12], 6, 1700485571);
      d = md5ii(d, a, b, c, x[i + 3], 10, -1894986606);
      c = md5ii(c, d, a, b, x[i + 10], 15, -1051523);
      b = md5ii(b, c, d, a, x[i + 1], 21, -2054922799);
      a = md5ii(a, b, c, d, x[i + 8], 6, 1873313359);
      d = md5ii(d, a, b, c, x[i + 15], 10, -30611744);
      c = md5ii(c, d, a, b, x[i + 6], 15, -1560198380);
      b = md5ii(b, c, d, a, x[i + 13], 21, 1309151649);
      a = md5ii(a, b, c, d, x[i + 4], 6, -145523070);
      d = md5ii(d, a, b, c, x[i + 11], 10, -1120210379);
      c = md5ii(c, d, a, b, x[i + 2], 15, 718787259);
      b = md5ii(b, c, d, a, x[i + 9], 21, -343485551);
      a = safeAdd(a, olda); b = safeAdd(b, oldb); c = safeAdd(c, oldc); d = safeAdd(d, oldd);
    }
    return [a, b, c, d];
  }

  function str2binl(str: string): number[] {
    const bin: number[] = [];
    const mask = (1 << 8) - 1;
    for (let i = 0; i < str.length * 8; i += 8) {
      bin[i >> 5] |= (str.charCodeAt(i / 8) & mask) << (i % 32);
    }
    return bin;
  }

  function binl2hex(binarray: number[]): string {
    const hexTab = '0123456789abcdef';
    let str = '';
    for (let i = 0; i < binarray.length * 32; i += 8) {
      str += hexTab.charAt((binarray[i >> 5] >>> (i % 32 + 4)) & 0xf) +
             hexTab.charAt((binarray[i >> 5] >>> (i % 32)) & 0xf);
    }
    return str;
  }

  return binl2hex(binlMD5(str2binl(input), input.length * 8));
}

const API_BASE_URL = env.apiBaseUrl;

/**
 * Normalize Portal Widget API field names to match our TypeScript types.
 * API returns: name, images[], image  →  We expect: title, image_url
 */
function normalizeProduct(raw: Record<string, any>): Product {
  return {
    id: raw.id,
    title: raw.name || raw.title || '',
    price: raw.price || '',
    compare_at_price: raw.compare_at_price ?? undefined,
    image_url: Array.isArray(raw.images) && raw.images.length > 0 ? raw.images[0] : (raw.image_url || ''),
    collection: raw.collections?.value?.[0] ?? raw.collection ?? undefined,
    product_code: raw.product_code ?? raw.remote_obj_id ?? undefined,
  };
}

function normalizeProductVariant(raw: Record<string, any>): ProductVariant {
  return {
    id: raw.id,
    title: raw.name || raw.title || '',
    price: raw.price || '',
    compare_at_price: raw.compare_at_price ?? undefined,
    sku: raw.sku ?? undefined,
    image_url: raw.image || raw.image_url || undefined,
  };
}

function normalizeBasketProduct(raw: Record<string, any>): BasketProduct {
  return {
    id: raw.id,
    quantity: raw.quantity,
    discount_val: raw.discount_val ?? 0,
    product: normalizeProduct(raw.product || {}),
    product_variant: normalizeProductVariant(raw.product_variant || {}),
    product_option: raw.product_option ?? undefined,
  };
}

function normalizeWishProduct(raw: Record<string, any>): WishProduct {
  return {
    id: raw.id,
    product: normalizeProduct(raw.product || {}),
    product_variant: normalizeProductVariant(raw.product_variant || {}),
  };
}

class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<void> | null = null;
  private tokenStore: { accessToken: string; refreshToken: string } | null = null;
  private customerId: number | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  setTokenStore(tokens: { accessToken: string; refreshToken: string }, customerId?: number): void {
    this.tokenStore = { ...tokens };
    if (customerId !== undefined) this.customerId = customerId;
  }

  getAccessToken(): string | null {
    return this.tokenStore?.accessToken ?? null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        // Capture 5xx errors to Sentry
        if (response.status >= 500) {
          const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
          captureError(error, {
            endpoint,
            method: options.method || 'GET',
            status: response.status,
            statusText: response.statusText,
          });
        }

        // Attempt to extract structured error code from JSON body (e.g., 410 gone)
        let parsedCode: string | undefined;
        try {
          const body = await response.json();
          if (body?.error?.code && typeof body.error.code === 'string') {
            parsedCode = body.error.code;
          }
        } catch {
          // Non-JSON bodies (HTML error pages from reverse proxy, etc.) are silently ignored
        }

        // Throw ApiError for all HTTP errors (preserves existing behavior)
        const apiError: ApiError = {
          error: response.statusText,
          status: response.status,
          ...(parsedCode !== undefined && { code: parsedCode }),
        };
        throw apiError;
      }

      return response.json();
    } catch (err) {
      // Capture network errors (e.g., no internet, CORS, DNS failure)
      if (err instanceof TypeError && err.message.includes('fetch')) {
        const networkError = new Error(`Network error: ${err.message}`);
        captureError(networkError, { endpoint, method: options.method || 'GET' });
      }

      // Re-throw the original error to preserve existing behavior
      throw err;
    }
  }

  async joinScheduledCall(roomKey: string, participant: 'customer' | 'seller' = 'customer'): Promise<JoinResponse> {
    // Demo mode interception
    if (env.demoMode && roomKey.startsWith('demo')) {
      // Scenario routing
      if (roomKey === 'demo-error') {
        const apiError: ApiError = { error: 'Demo error scenario', status: 500 };
        throw apiError;
      }
      if (roomKey === 'demo-loading') {
        // Never resolve to simulate indefinite loading
        await new Promise(() => {});
      }
      // Default: return mock data after brief delay to simulate network
      await new Promise(resolve => setTimeout(resolve, 500));
      return MOCK_JOIN_RESPONSE;
    }

    return this.request<JoinResponse>(
      `/v1/scheduled_calls/${roomKey}/join?participant=${participant}`
    );
  }

  /**
   * W7: tell Portal the customer actually entered the consultation in chat-only
   * mode. Every other mode connects to the Twilio room, and Portal learns about
   * it from the `participant-connected` webhook — chat mode never becomes a room
   * participant, so without this the appointment is indistinguishable from a
   * no-show. Idempotent server-side and deliberately fire-and-forget: a failed
   * metric ping must never block the customer from joining.
   */
  async markCustomerJoined(roomKey: string): Promise<CustomerJoinedResponse | null> {
    if (env.demoMode) return null;

    try {
      return await this.request<CustomerJoinedResponse>(
        `/v1/scheduled_calls/${roomKey}/customer_joined`,
        { method: 'POST' }
      );
    } catch {
      return null;
    }
  }

  /**
   * W5: tell Portal which mode the customer has moved into, so the seller can be
   * told about it. `video` / `audio` / `chat` — the same three words the tabs use.
   *
   * Fire-and-forget, like markCustomerJoined above: this reports a fact about an
   * interface that has ALREADY changed, so a failed request must not surface to
   * the customer or hold anything up. Portal is idempotent — a repeat of the
   * mode it already holds is stored once and pushed zero times.
   */
  async reportParticipantMode(roomKey: string, mode: CommunicationMode): Promise<void> {
    if (env.demoMode) return;

    try {
      await this.request(
        `/v1/scheduled_calls/${roomKey}/participant_mode`,
        { method: 'POST', body: JSON.stringify({ mode }) }
      );
    } catch {
      // Intentionally silent — see above.
    }
  }

  async getProducts(auth: WidgetAuth, limit: number = 10, callId?: number): Promise<ProductsResponse> {
    // Demo mode interception (SPEC-030)
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { products: MOCK_PRODUCTS };
    }

    // Append call_id when present so the backend can filter products to the attached Look
    const callParam = callId !== undefined ? `&call_id=${callId}` : '';

    // Widget API returns array directly (envelope unwrapped by widgetRequest)
    const raw = await this.widgetRequest<Array<Record<string, any>>>(
      `/products?limit=${limit}${callParam}`,
      {},
      auth
    );
    return { products: raw.map(normalizeProduct) };
  }

  async getProductDetail(productId: number, auth: WidgetAuth, kind?: string): Promise<ProductDetailResponse> {
    // Demo mode interception (SPEC-034, SPEC-036)
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 400));
      // Route to different mocks based on productId for UI testing:
      // id=1 -> MOCK_PRODUCT_DETAIL (full product with options)
      // id=2 -> MOCK_PRODUCT_DETAIL_NO_VARIANTS (no variants product)
      // id=3 -> MOCK_COMBO_PARENT_PRODUCT (combo bundle parent — AC-23)
      // id=1001 -> MOCK_COMBO_CHILD_1 (combo child: Size Selection — AC-23, AC-24)
      // id=1002 -> MOCK_COMBO_CHILD_2 (combo child: Color Selection — AC-23, AC-24)
      let mockProduct;
      if (productId === 2) {
        mockProduct = MOCK_PRODUCT_DETAIL_NO_VARIANTS;
      } else if (productId === 3) {
        mockProduct = MOCK_COMBO_PARENT_PRODUCT;
      } else if (productId === 1001) {
        mockProduct = MOCK_COMBO_CHILD_1;
      } else if (productId === 1002) {
        mockProduct = MOCK_COMBO_CHILD_2;
      } else {
        mockProduct = MOCK_PRODUCT_DETAIL;
      }
      return { status: 'ok', response: mockProduct };
    }

    const params = kind ? `?kind=${encodeURIComponent(kind)}` : '';
    // Widget API envelope unwrapped by widgetRequest — returns ProductDetail directly
    const product = await this.widgetRequest<ProductDetail>(
      `/products/${productId}${params}`,
      {},
      auth
    );
    return { status: 'ok', response: product };
  }

  // `callId` is what ties the view to this appointment. Without it Portal falls
  // back to looking up an "online" call, and an appointment never qualifies —
  // its status stays `created` — so the row landed with no call and the seller's
  // Viewed tab (which reads strictly by call id) stayed empty.
  async markProductViewed(callId: number, productId: number, auth: WidgetAuth): Promise<void> {
    if (env.demoMode) return;
    await this.widgetRequest<unknown>(
      '/recent_products',
      { method: 'POST', body: JSON.stringify({ call_id: callId, product_id: productId }) },
      auth
    );
  }

  async submitFeedback(payload: FeedbackPayload, auth: WidgetAuth): Promise<FeedbackResponse> {
    // Demo mode interception (SPEC-030)
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, review_id: 99999 };
    }

    return this.widgetRequest<FeedbackResponse>(
      '/reviews',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      auth
    );
  }

  async submitRecordingConsent(
    roomKey: string,
    consented: boolean
  ): Promise<RecordingConsentResponse> {
    // Demo mode: return mock success without network call
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    }

    return this.request<RecordingConsentResponse>(
      `/v1/scheduled_calls/${roomKey}/recording_consent`,
      {
        method: 'POST',
        body: JSON.stringify({ consented }),
      }
    );
  }

  /**
   * Widget API request method (separate base URL from public API).
   * Derives widget URL from apiBaseUrl by replacing /api suffix with /vece/api/widget/v1.
   * Adds Authorization, X-Timestamp, and X-Signature headers.
   * Handles 401 "Signature has expired" by refreshing the token and retrying.
   */
  private async widgetRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    auth: { accessToken: string; serviceId: string | number }
  ): Promise<T> {
    const widgetBaseUrl = this.getWidgetBaseUrl();
    const accessToken = this.tokenStore?.accessToken ?? auth.accessToken;

    const doFetch = async (token: string): Promise<Response> => {
      const url = `${widgetBaseUrl}${endpoint}`;
      const timestamp = Math.floor(Date.now() / 1000).toString();
      const signature = md5(`${auth.serviceId}${timestamp}`);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Timestamp': timestamp,
        'X-Signature': signature,
      };
      if (this.customerId) {
        headers['X-ID-Customer'] = String(this.customerId);
      }

      return fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...(options.headers as Record<string, string>),
        },
      });
    };

    try {
      let response = await doFetch(accessToken);

      // Handle 401 "Signature has expired" with token refresh
      if (response.status === 401) {
        const body = await response.clone().text();
        if (body.includes('Signature has expired') && this.tokenStore) {
          // If a refresh is already in progress, wait for it
          if (this.refreshPromise) {
            await this.refreshPromise;
          } else {
            // Start a new refresh
            this.refreshPromise = this.refreshToken(auth.serviceId);
            try {
              await this.refreshPromise;
            } catch {
              this.tokenStore = null;
              this.refreshPromise = null;
              const apiError: ApiError = { error: response.statusText, status: 401 };
              throw apiError;
            }
            this.refreshPromise = null;
          }

          // Retry with new token
          const newToken = this.tokenStore?.accessToken ?? auth.accessToken;
          response = await doFetch(newToken);
        }
      }

      if (!response.ok) {
        if (response.status >= 500) {
          const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
          captureError(error, {
            endpoint,
            method: options.method || 'GET',
            status: response.status,
            statusText: response.statusText,
          });
        }
        const apiError: ApiError = {
          error: response.statusText,
          status: response.status,
        };
        throw apiError;
      }

      // All Widget API responses are wrapped in an envelope: { status, response: <data> }
      const data = await response.json();
      return data.response !== undefined ? data.response : data;
    } catch (err) {
      if (err instanceof TypeError && err.message.includes('fetch')) {
        const networkError = new Error(`Network error: ${err.message}`);
        captureError(networkError, { endpoint, method: options.method || 'GET' });
      }
      throw err;
    }
  }

  private getWidgetBaseUrl(): string {
    const replaced = this.baseUrl.replace(/\/api\/?$/, '/vece/api/widget/v1');
    // If baseUrl didn't end with /api, the replace had no effect — append the widget path
    return replaced === this.baseUrl
      ? `${this.baseUrl}/vece/api/widget/v1`
      : replaced;
  }

  private async refreshToken(serviceId: string | number): Promise<void> {
    if (!this.tokenStore) return;

    const widgetBaseUrl = this.getWidgetBaseUrl();
    const url = `${widgetBaseUrl}/customers/refresh_token`;
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const signature = md5(`${serviceId}${timestamp}`);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.tokenStore.refreshToken}`,
        'X-Timestamp': timestamp,
        'X-Signature': signature,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const data: { status: string; response: { access_token: string; refresh_token: string } } = await response.json();
    this.tokenStore = {
      accessToken: data.response.access_token,
      refreshToken: data.response.refresh_token,
    };
  }

  // --- Customer End-Call Methods (SPEC-054) ---
  // single /stop covers the full scheduled-call lifecycle; see call-18148 incident / SPEC-055
  // Portal's hardened CallOperation::Stop accepts: customer-first (no seller ever joined);
  // seller joined and still present; seller joined and left. No endpoint-selection branch needed.

  async stopCall(
    callId: number,
    auth: WidgetAuth,
    opts?: CallStopPayload
  ): Promise<void> {
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 100));
      return;
    }
    const body: Record<string, unknown> = {};
    if (opts?.by) body.by = opts.by;
    await this.widgetRequest<unknown>(
      `/calls/${callId}/stop`,
      { method: 'POST', body: JSON.stringify(body) },
      auth
    );
  }

  // --- Basket API Methods (SPEC-032) ---

  async getBasketProducts(
    callId: number,
    auth: { accessToken: string; serviceId: string | number }
  ): Promise<BasketResponse> {
    // Demo mode interception
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return MOCK_BASKET_RESPONSE;
    }

    const raw = await this.widgetRequest<{ products: Record<string, any>[]; total: string }>(
      `/calls/${callId}/basket_products`,
      {},
      auth
    );
    return { products: raw.products.map(normalizeBasketProduct), total: raw.total };
  }

  async addBasketProduct(
    callId: number,
    identifier: { variantId?: number; remoteObjId?: string },
    auth: { accessToken: string; serviceId: string | number },
    options?: { quantity?: number; discountVal?: number; selectedProductOptions?: Record<string, string> }
  ): Promise<BasketResponse> {
    // Demo mode interception
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return MOCK_BASKET_RESPONSE;
    }

    // Prefer variant_id when available (PDP with explicit variant selection),
    // fall back to remote_obj_id (product list reserve — Portal resolves by Shopify product_code)
    const body: Record<string, unknown> = {};
    if (identifier.variantId !== undefined) {
      body.variant_id = identifier.variantId;
    } else if (identifier.remoteObjId !== undefined) {
      body.remote_obj_id = identifier.remoteObjId;
    } else {
      throw new Error('addBasketProduct requires either variantId or remoteObjId');
    }
    if (options?.quantity !== undefined) body.quantity = options.quantity;
    if (options?.discountVal !== undefined) body.discount_val = options.discountVal;

    // SPEC-035: Pass option_id and option_value when product options are selected.
    // Portal API accepts only a single option_id/option_value pair (matching Angular widget BasketPostHandler).
    // If multiple option groups are selected, only the first key is sent.
    if (options?.selectedProductOptions && Object.keys(options.selectedProductOptions).length > 0) {
      const firstKey = Object.keys(options.selectedProductOptions)[0];
      body.option_id = firstKey;
      body.option_value = options.selectedProductOptions[firstKey];
    }

    const raw = await this.widgetRequest<{ products: Record<string, any>[]; total: string }>(
      `/calls/${callId}/basket_products`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      auth
    );
    return { products: raw.products.map(normalizeBasketProduct), total: raw.total };
  }

  async removeBasketProduct(
    callId: number,
    basketProductId: number,
    auth: { accessToken: string; serviceId: string | number }
  ): Promise<BasketResponse> {
    // Demo mode interception
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { products: [], total: '0.00' };
    }

    const raw = await this.widgetRequest<{ products: Record<string, any>[]; total: string }>(
      `/calls/${callId}/basket_products/${basketProductId}`,
      { method: 'DELETE' },
      auth
    );
    return { products: raw.products.map(normalizeBasketProduct), total: raw.total };
  }

  // --- Wish Products API Methods (SPEC-033) ---

  async getWishProducts(
    callId: number,
    auth: { accessToken: string; serviceId: string | number }
  ): Promise<WishProductsResponse> {
    // Demo mode interception
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return MOCK_WISH_PRODUCTS_RESPONSE;
    }

    // Widget API returns array directly (envelope unwrapped by widgetRequest)
    const raw = await this.widgetRequest<Record<string, any>[]>(
      `/calls/${callId}/wish_products`,
      {},
      auth
    );
    return { products: raw.map(normalizeWishProduct) };
  }

  async addWishProduct(
    callId: number,
    identifier: { variantId?: number; remoteObjId?: string },
    auth: { accessToken: string; serviceId: string | number }
  ): Promise<WishProductsResponse> {
    // Demo mode interception
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return MOCK_WISH_PRODUCTS_RESPONSE;
    }

    // Build body: prefer variant_id when available (PDP), fall back to remote_obj_id (product list)
    const body: Record<string, unknown> = {};
    if (identifier.variantId !== undefined) {
      body.variant_id = identifier.variantId;
    } else if (identifier.remoteObjId !== undefined) {
      body.remote_obj_id = identifier.remoteObjId;
    } else {
      throw new Error('addWishProduct requires either variantId or remoteObjId');
    }

    // Widget API returns array directly (envelope unwrapped by widgetRequest)
    const raw = await this.widgetRequest<Record<string, any>[]>(
      `/calls/${callId}/wish_products`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      auth
    );
    return { products: raw.map(normalizeWishProduct) };
  }

  async removeWishProduct(
    callId: number,
    wishProductId: number,
    auth: { accessToken: string; serviceId: string | number }
  ): Promise<WishProductsResponse> {
    // Demo mode interception
    if (env.demoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { products: [] };
    }

    // Widget API returns array directly (envelope unwrapped by widgetRequest)
    const raw = await this.widgetRequest<Record<string, any>[]>(
      `/calls/${callId}/wish_products/${wishProductId}`,
      { method: 'DELETE' },
      auth
    );
    return { products: raw.map(normalizeWishProduct) };
  }
}

export const apiClient = new ApiClient();
export default ApiClient;
